// HKDSE English Grammar Learning Cards Data
// Systematic organization for Supabase migration

export const grammarCategories = {
  partsOfSpeech: {
    id: 'parts-of-speech',
    title: '詞性分析 (Parts of Speech)',
    icon: '🏷️',
    color: 'bg-blue-500',
    description: 'Complete analysis of all word types in English',
    hkdseRelevance: 'Paper 1 & 2 - Essential for reading comprehension and writing'
  },
  verbTenses: {
    id: 'verb-tenses',
    title: '動詞時態 (Verb Tenses)',
    icon: '⏰',
    color: 'bg-green-500',
    description: '12 major tenses with visual timeline',
    hkdseRelevance: 'Paper 1, 2 & 4 - Critical for all language skills'
  },
  sentenceStructure: {
    id: 'sentence-structure',
    title: '句子結構 (Sentence Structure)',
    icon: '🏗️',
    color: 'bg-purple-500',
    description: 'Five basic sentence patterns and complex structures',
    hkdseRelevance: 'Paper 2 - Essential for effective writing'
  },
  clausesPhrases: {
    id: 'clauses-phrases',
    title: '子句與片語 (Clauses & Phrases)',
    icon: '🔗',
    color: 'bg-orange-500',
    description: 'Independent, dependent clauses and phrase types',
    hkdseRelevance: 'Paper 1 & 2 - Advanced sentence construction'
  },
  commonErrors: {
    id: 'common-errors',
    title: '常見錯誤 (Common Errors)',
    icon: '⚠️',
    color: 'bg-red-500',
    description: 'Typical mistakes and how to avoid them',
    hkdseRelevance: 'All Papers - Error identification and correction'
  },
  practiceExercises: {
    id: 'practice-exercises',
    title: '綜合練習 (Practice)',
    icon: '📝',
    color: 'bg-indigo-500',
    description: 'Interactive exercises and practice questions',
    hkdseRelevance: 'Exam preparation and skill reinforcement'
  },
  essToolbox: {
    id: 'ess-toolbox',
    title: 'ESS Toolbox',
    icon: '🔧',
    color: 'bg-teal-500',
    description: 'English Sentence Structure Teaching Toolbox',
    hkdseRelevance: 'Advanced sentence analysis and practice'
  },
  linguisticAnalysis: {
    id: 'linguistic-analysis',
    title: '語言分析 (Linguistic Analysis)',
    icon: '📊',
    color: 'bg-indigo-500',
    description: 'Advanced sentence structure analysis with Legend Structure',
    hkdseRelevance: 'Paper 1 & 2 - Understanding complex sentence patterns'
  }
};

// Parts of Speech Data
export const partsOfSpeechData = {
  nouns: {
    id: 'nouns',
    title: '名詞 (Nouns)',
    definition: 'Words that name people, places, things, or ideas',
    types: [
      {
        type: 'proper',
        name: '專有名詞 (Proper Nouns)',
        definition: 'Names of specific people, places, or things',
        examples: ['Hong Kong', 'Mary', 'Christmas', 'Monday'],
        rules: ['Always capitalize the first letter', 'No article needed for most proper nouns'],
        hkdseNote: 'Common in Paper 1 reading passages about specific locations or people'
      },
      {
        type: 'common',
        name: '普通名詞 (Common Nouns)',
        definition: 'General names for people, places, things',
        examples: ['student', 'school', 'book', 'city'],
        rules: ['Use articles (a, an, the)', 'Can be singular or plural'],
        hkdseNote: 'Most frequently tested in grammar sections'
      },
      {
        type: 'abstract',
        name: '抽象名詞 (Abstract Nouns)',
        definition: 'Names for ideas, qualities, or states',
        examples: ['happiness', 'freedom', 'knowledge', 'success'],
        rules: ['Usually uncountable', 'No plural form for most'],
        hkdseNote: 'Important for Paper 2 writing - expressing complex ideas'
      },
      {
        type: 'concrete',
        name: '具體名詞 (Concrete Nouns)',
        definition: 'Names for things you can touch or see',
        examples: ['table', 'computer', 'flower', 'building'],
        rules: ['Can be counted', 'Have singular and plural forms'],
        hkdseNote: 'Basic vocabulary for descriptive writing'
      },
      {
        type: 'collective',
        name: '集合名詞 (Collective Nouns)',
        definition: 'Names for groups of people or things',
        examples: ['team', 'family', 'government', 'class'],
        rules: ['Can be singular or plural depending on context', 'Agreement with verb is important'],
        hkdseNote: 'Tricky for subject-verb agreement questions'
      }
    ]
  },
  pronouns: {
    id: 'pronouns',
    title: '代詞 (Pronouns)',
    definition: 'Words that replace nouns to avoid repetition',
    types: [
      {
        type: 'personal',
        name: '人稱代詞 (Personal Pronouns)',
        definition: 'Replace specific people or things',
        examples: {
          subject: ['I', 'you', 'he', 'she', 'it', 'we', 'they'],
          object: ['me', 'you', 'him', 'her', 'it', 'us', 'them']
        },
        rules: ['Subject pronouns before verbs', 'Object pronouns after verbs/prepositions'],
        hkdseNote: 'Frequently tested in error correction exercises'
      },
      {
        type: 'possessive',
        name: '所有格代詞 (Possessive Pronouns)',
        definition: 'Show ownership or possession',
        examples: {
          adjective: ['my', 'your', 'his', 'her', 'its', 'our', 'their'],
          pronoun: ['mine', 'yours', 'his', 'hers', 'ours', 'theirs']
        },
        rules: ['Possessive adjectives + noun', 'Possessive pronouns stand alone'],
        hkdseNote: 'Common in Paper 1 cloze passages'
      },
      {
        type: 'reflexive',
        name: '反身代詞 (Reflexive Pronouns)',
        definition: 'Refer back to the subject',
        examples: ['myself', 'yourself', 'himself', 'herself', 'itself', 'ourselves', 'yourselves', 'themselves'],
        rules: ['Same person as subject', 'Used for emphasis or when action affects doer'],
        hkdseNote: 'Important for Paper 2 writing variety'
      },
      {
        type: 'demonstrative',
        name: '指示代詞 (Demonstrative Pronouns)',
        definition: 'Point to specific things',
        examples: ['this', 'that', 'these', 'those'],
        rules: ['This/these for near', 'That/those for far', 'Singular/plural agreement'],
        hkdseNote: 'Used in Paper 4 speaking for referencing'
      },
      {
        type: 'indefinite',
        name: '不定代詞 (Indefinite Pronouns)',
        definition: 'Refer to non-specific people or things',
        examples: ['someone', 'anyone', 'everyone', 'something', 'nothing', 'all', 'some', 'many'],
        rules: ['Usually singular verbs', 'Some can be plural'],
        hkdseNote: 'Complex subject-verb agreement rules'
      }
    ]
  },
  verbs: {
    id: 'verbs',
    title: '動詞 (Verbs)',
    definition: 'Words that express actions, states, or occurrences',
    types: [
      {
        type: 'action',
        name: '動作動詞 (Action Verbs)',
        definition: 'Express physical or mental actions',
        examples: ['run', 'think', 'write', 'study', 'play'],
        rules: ['Can be transitive or intransitive', 'Form different tenses'],
        hkdseNote: 'Most common in narrative writing'
      },
      {
        type: 'linking',
        name: '連繫動詞 (Linking Verbs)',
        definition: 'Connect subject to complement',
        examples: ['be', 'seem', 'appear', 'become', 'feel', 'look', 'sound'],
        rules: ['Followed by adjectives or nouns', 'No direct object'],
        hkdseNote: 'Important for descriptive writing'
      },
      {
        type: 'auxiliary',
        name: '助動詞 (Auxiliary Verbs)',
        definition: 'Help main verbs form tenses, questions, negatives',
        examples: ['be', 'have', 'do', 'will', 'would', 'can', 'could', 'may', 'might', 'must', 'should'],
        rules: ['Combine with main verbs', 'Form questions and negatives'],
        hkdseNote: 'Essential for all tense formations'
      },
      {
        type: 'transitive',
        name: '及物動詞 (Transitive Verbs)',
        definition: 'Require direct objects',
        examples: ['eat (food)', 'read (book)', 'write (letter)', 'buy (car)'],
        rules: ['Must have direct object', 'Can be made passive'],
        hkdseNote: 'Important for active/passive voice'
      },
      {
        type: 'intransitive',
        name: '不及物動詞 (Intransitive Verbs)',
        definition: 'Do not require direct objects',
        examples: ['sleep', 'arrive', 'die', 'laugh', 'cry'],
        rules: ['No direct object needed', 'Cannot be made passive'],
        hkdseNote: 'Cannot form passive voice'
      }
    ]
  },
  adjectives: {
    id: 'adjectives',
    title: '形容詞 (Adjectives)',
    definition: 'Words that describe or modify nouns',
    types: [
      {
        type: 'descriptive',
        name: '描述性形容詞 (Descriptive Adjectives)',
        definition: 'Describe qualities or characteristics',
        examples: ['beautiful', 'large', 'intelligent', 'red', 'happy'],
        rules: ['Usually before nouns', 'Can be comparative/superlative'],
        hkdseNote: 'Essential for Paper 2 descriptive writing'
      },
      {
        type: 'quantitative',
        name: '數量形容詞 (Quantitative Adjectives)',
        definition: 'Indicate quantity or amount',
        examples: ['many', 'few', 'several', 'some', 'all', 'every'],
        rules: ['Agreement with countable/uncountable nouns'],
        hkdseNote: 'Important for Paper 1 cloze exercises'
      },
      {
        type: 'possessive',
        name: '所有格形容詞 (Possessive Adjectives)',
        definition: 'Show ownership',
        examples: ['my', 'your', 'his', 'her', 'its', 'our', 'their'],
        rules: ['Always followed by nouns', 'No apostrophe'],
        hkdseNote: 'Distinguish from possessive pronouns'
      }
    ]
  },
  adverbs: {
    id: 'adverbs',
    title: '副詞 (Adverbs)',
    definition: 'Words that modify verbs, adjectives, or other adverbs',
    types: [
      {
        type: 'manner',
        name: '方式副詞 (Adverbs of Manner)',
        definition: 'Describe how something is done',
        examples: ['quickly', 'carefully', 'beautifully', 'well', 'badly'],
        rules: ['Usually end in -ly', 'Can come before or after verb'],
        hkdseNote: 'Add variety to writing style'
      },
      {
        type: 'time',
        name: '時間副詞 (Adverbs of Time)',
        definition: 'Indicate when something happens',
        examples: ['now', 'then', 'yesterday', 'soon', 'already', 'never'],
        rules: ['Various positions in sentence', 'Some used with specific tenses'],
        hkdseNote: 'Important for tense usage'
      },
      {
        type: 'frequency',
        name: '頻率副詞 (Adverbs of Frequency)',
        definition: 'Show how often something happens',
        examples: ['always', 'usually', 'often', 'sometimes', 'rarely', 'never'],
        rules: ['Usually before main verb', 'After "be" verb'],
        hkdseNote: 'Common in Paper 1 and 2'
      },
      {
        type: 'degree',
        name: '程度副詞 (Adverbs of Degree)',
        definition: 'Show intensity or degree',
        examples: ['very', 'quite', 'rather', 'extremely', 'completely', 'hardly'],
        rules: ['Before adjectives/adverbs they modify'],
        hkdseNote: 'Enhance descriptive writing'
      }
    ]
  }
};

// Verb Tenses Data with Visual Timeline
export const verbTensesData = {
  timeline: {
    past: ['Past Perfect', 'Past Continuous', 'Simple Past'],
    present: ['Present Perfect', 'Present Continuous', 'Simple Present'],
    future: ['Future Perfect', 'Future Continuous', 'Simple Future']
  },
  tenses: [
    {
      id: 'simple-present',
      name: 'Simple Present',
      chineseName: '現在簡單式',
      structure: 'Subject + base form (+ s/es for 3rd person singular)',
      examples: [
        'I study English every day.',
        'She works in a bank.',
        'They live in Hong Kong.'
      ],
      usage: [
        'Habits and routines',
        'General truths',
        'Scheduled events'
      ],
      timeMarkers: ['every day', 'usually', 'always', 'never', 'on Mondays'],
      hkdseNote: 'Most basic tense - foundation for all others',
      commonErrors: ['Forgetting -s for 3rd person', 'Using with time expressions incorrectly']
    },
    {
      id: 'present-continuous',
      name: 'Present Continuous',
      chineseName: '現在進行式',
      structure: 'Subject + am/is/are + verb-ing',
      examples: [
        'I am studying now.',
        'She is working on a project.',
        'They are playing football.'
      ],
      usage: [
        'Actions happening now',
        'Temporary situations',
        'Future arrangements'
      ],
      timeMarkers: ['now', 'at the moment', 'currently', 'today', 'this week'],
      hkdseNote: 'Often confused with simple present',
      commonErrors: ['Using with stative verbs', 'Incorrect -ing forms']
    },
    {
      id: 'present-perfect',
      name: 'Present Perfect',
      chineseName: '現在完成式',
      structure: 'Subject + have/has + past participle',
      examples: [
        'I have finished my homework.',
        'She has lived here for 5 years.',
        'They have never been to Japan.'
      ],
      usage: [
        'Past actions affecting present',
        'Experiences up to now',
        'Actions continuing to present'
      ],
      timeMarkers: ['already', 'just', 'ever', 'never', 'since', 'for', 'yet'],
      hkdseNote: 'Very important for HKDSE - shows connection between past and present',
      commonErrors: ['Using with specific past time', 'Confusing with simple past']
    },
    {
      id: 'simple-past',
      name: 'Simple Past',
      chineseName: '過去簡單式',
      structure: 'Subject + past form of verb',
      examples: [
        'I studied yesterday.',
        'She worked late last night.',
        'They visited the museum.'
      ],
      usage: [
        'Completed actions in past',
        'Past habits',
        'Sequence of past events'
      ],
      timeMarkers: ['yesterday', 'last week', 'ago', 'in 2020', 'when I was young'],
      hkdseNote: 'Essential for narrative writing',
      commonErrors: ['Irregular verb forms', 'Using with present perfect markers']
    },
    {
      id: 'past-continuous',
      name: 'Past Continuous',
      chineseName: '過去進行式',
      structure: 'Subject + was/were + verb-ing',
      examples: [
        'I was studying when you called.',
        'She was working at 9 PM.',
        'They were playing when it started raining.'
      ],
      usage: [
        'Actions in progress in past',
        'Background actions',
        'Interrupted actions'
      ],
      timeMarkers: ['while', 'when', 'at that time', 'all day yesterday'],
      hkdseNote: 'Good for creating atmosphere in stories',
      commonErrors: ['Using with stative verbs', 'Confusing with simple past']
    },
    {
      id: 'past-perfect',
      name: 'Past Perfect',
      chineseName: '過去完成式',
      structure: 'Subject + had + past participle',
      examples: [
        'I had finished before you arrived.',
        'She had already left when I called.',
        'They had never seen such a beautiful sunset.'
      ],
      usage: [
        'Actions completed before another past action',
        'Past experiences before a past time',
        'Cause and effect in past'
      ],
      timeMarkers: ['before', 'after', 'by the time', 'already', 'just', 'never'],
      hkdseNote: 'Shows sequence of past events clearly',
      commonErrors: ['Overusing when simple past is enough', 'Wrong sequence of events']
    },
    {
      id: 'simple-future',
      name: 'Simple Future',
      chineseName: '未來簡單式',
      structure: 'Subject + will + base form',
      examples: [
        'I will study tomorrow.',
        'She will work next week.',
        'They will visit us soon.'
      ],
      usage: [
        'Future predictions',
        'Spontaneous decisions',
        'Promises and offers'
      ],
      timeMarkers: ['tomorrow', 'next week', 'soon', 'in the future', 'later'],
      hkdseNote: 'Basic future form - most commonly used',
      commonErrors: ['Using "will" with time clauses', 'Confusing with "going to"']
    },
    {
      id: 'future-continuous',
      name: 'Future Continuous',
      chineseName: '未來進行式',
      structure: 'Subject + will be + verb-ing',
      examples: [
        'I will be studying at 8 PM.',
        'She will be working tomorrow.',
        'They will be traveling next month.'
      ],
      usage: [
        'Actions in progress at future time',
        'Future arrangements',
        'Polite inquiries about plans'
      ],
      timeMarkers: ['at this time tomorrow', 'all day tomorrow', 'when you arrive'],
      hkdseNote: 'Less common but useful for sophisticated writing',
      commonErrors: ['Using with stative verbs', 'Unnecessary complexity']
    },
    {
      id: 'future-perfect',
      name: 'Future Perfect',
      chineseName: '未來完成式',
      structure: 'Subject + will have + past participle',
      examples: [
        'I will have finished by 6 PM.',
        'She will have graduated by next year.',
        'They will have left before you arrive.'
      ],
      usage: [
        'Actions completed before future time',
        'Future achievements',
        'Predictions about completion'
      ],
      timeMarkers: ['by', 'before', 'by the time', 'by then'],
      hkdseNote: 'Advanced tense for high-level writing',
      commonErrors: ['Using when simple future is enough', 'Confusing time references']
    }
  ]
};

// Sentence Structure Data
export const sentenceStructureData = {
  basicPatterns: [
    {
      id: 'sv',
      pattern: 'S + V',
      name: 'Subject + Verb',
      chineseName: '主語 + 動詞',
      description: 'The simplest sentence pattern',
      examples: [
        'Birds fly.',
        'Students study.',
        'Time passes.'
      ],
      hkdseNote: 'Foundation for all other patterns'
    },
    {
      id: 'svo',
      pattern: 'S + V + O',
      name: 'Subject + Verb + Object',
      chineseName: '主語 + 動詞 + 賓語',
      description: 'Most common sentence pattern',
      examples: [
        'Students read books.',
        'She plays piano.',
        'They watch movies.'
      ],
      hkdseNote: 'Most frequently used in writing'
    },
    {
      id: 'svc',
      pattern: 'S + V + C',
      name: 'Subject + Verb + Complement',
      chineseName: '主語 + 動詞 + 補語',
      description: 'Subject complement describes subject',
      examples: [
        'She is beautiful.',
        'The food tastes good.',
        'He became a doctor.'
      ],
      hkdseNote: 'Important for descriptive writing'
    },
    {
      id: 'svoo',
      pattern: 'S + V + O + O',
      name: 'Subject + Verb + Indirect Object + Direct Object',
      chineseName: '主語 + 動詞 + 間接賓語 + 直接賓語',
      description: 'Two objects - person and thing',
      examples: [
        'I gave him a book.',
        'She told me a story.',
        'They bought her a gift.'
      ],
      hkdseNote: 'Can be rewritten with prepositions'
    },
    {
      id: 'svoc',
      pattern: 'S + V + O + C',
      name: 'Subject + Verb + Object + Complement',
      chineseName: '主語 + 動詞 + 賓語 + 補語',
      description: 'Object complement describes object',
      examples: [
        'We elected him president.',
        'She painted the wall red.',
        'They found the movie boring.'
      ],
      hkdseNote: 'Advanced pattern for sophisticated writing'
    }
  ],
  sentenceTypes: [
    {
      id: 'simple',
      name: 'Simple Sentence',
      chineseName: '簡單句',
      definition: 'One independent clause',
      structure: 'One subject + one predicate',
      examples: [
        'The cat sleeps.',
        'Students are studying hard.',
        'My friend and I went to the movies.'
      ],
      hkdseNote: 'Clear and direct - good for emphasis'
    },
    {
      id: 'compound',
      name: 'Compound Sentence',
      chineseName: '複合句',
      definition: 'Two or more independent clauses',
      structure: 'Independent clause + conjunction + independent clause',
      examples: [
        'I study hard, but the exam is difficult.',
        'She likes reading, and he enjoys writing.',
        'We can go to the park, or we can stay home.'
      ],
      connectors: ['and', 'but', 'or', 'so', 'yet', 'for', 'nor'],
      hkdseNote: 'Shows relationship between ideas'
    },
    {
      id: 'complex',
      name: 'Complex Sentence',
      chineseName: '複雜句',
      definition: 'One independent + one or more dependent clauses',
      structure: 'Independent clause + subordinating conjunction + dependent clause',
      examples: [
        'Because it was raining, we stayed home.',
        'I will call you when I arrive.',
        'The book that you gave me is interesting.'
      ],
      subordinators: ['because', 'when', 'if', 'although', 'since', 'while', 'that', 'which', 'who'],
      hkdseNote: 'Shows cause-effect, time, condition relationships'
    }
  ],
  voiceTypes: [
    {
      id: 'active',
      name: 'Active Voice',
      chineseName: '主動語態',
      definition: 'Subject performs the action',
      structure: 'Subject + Verb + Object',
      examples: [
        'The teacher explained the lesson.',
        'Students completed the assignment.',
        'She wrote a beautiful poem.'
      ],
      advantages: ['Clear and direct', 'More engaging', 'Uses fewer words'],
      hkdseNote: 'Preferred for most writing - more dynamic'
    },
    {
      id: 'passive',
      name: 'Passive Voice',
      chineseName: '被動語態',
      definition: 'Subject receives the action',
      structure: 'Subject + be + past participle (+ by + agent)',
      examples: [
        'The lesson was explained by the teacher.',
        'The assignment was completed by students.',
        'A beautiful poem was written by her.'
      ],
      uses: ['Unknown agent', 'Unimportant agent', 'Formal writing', 'Scientific writing'],
      hkdseNote: 'Use sparingly - can make writing wordy'
    }
  ]
};

// Clauses and Phrases Data
export const clausesPhrasesData = {
  clauses: [
    {
      id: 'independent',
      name: 'Independent Clause',
      chineseName: '獨立子句',
      definition: 'Can stand alone as a complete sentence',
      structure: 'Subject + Predicate',
      examples: [
        'I love reading books.',
        'She studies every day.',
        'The weather is beautiful today.'
      ],
      characteristics: ['Complete thought', 'Can be a sentence by itself', 'Has subject and verb'],
      hkdseNote: 'Foundation of all sentence types'
    },
    {
      id: 'dependent',
      name: 'Dependent Clause',
      chineseName: '從屬子句',
      definition: 'Cannot stand alone - needs independent clause',
      structure: 'Subordinating word + Subject + Predicate',
      examples: [
        'because I love reading books',
        'when she studies every day',
        'although the weather is beautiful'
      ],
      types: ['Adverb clauses', 'Adjective clauses', 'Noun clauses'],
      hkdseNote: 'Adds complexity and sophistication to writing'
    },
    {
      id: 'relative',
      name: 'Relative Clause',
      chineseName: '關係子句',
      definition: 'Describes or gives information about a noun',
      structure: 'Relative pronoun + Subject + Predicate',
      types: [
        {
          type: 'defining',
          name: 'Defining Relative Clause',
          chineseName: '限定性關係子句',
          definition: 'Essential information - no commas',
          examples: [
            'The book that I bought is interesting.',
            'Students who study hard succeed.',
            'The place where we met is special.'
          ],
          hkdseNote: 'No commas - essential information'
        },
        {
          type: 'non-defining',
          name: 'Non-defining Relative Clause',
          chineseName: '非限定性關係子句',
          definition: 'Extra information - uses commas',
          examples: [
            'My brother, who lives in Canada, is visiting.',
            'Hong Kong, which is a vibrant city, attracts tourists.',
            'The teacher, whom we all respect, is retiring.'
          ],
          hkdseNote: 'Always use commas - extra information'
        }
      ],
      relativePronouns: ['who', 'whom', 'whose', 'which', 'that', 'where', 'when', 'why']
    }
  ],
  phrases: [
    {
      id: 'noun-phrase',
      name: 'Noun Phrase',
      chineseName: '名詞片語',
      definition: 'Group of words acting as a noun',
      structure: 'Determiner + Adjective + Noun + Prepositional phrase',
      examples: [
        'the beautiful red roses in the garden',
        'my best friend from school',
        'a very interesting book about history'
      ],
      functions: ['Subject', 'Object', 'Complement'],
      hkdseNote: 'Makes writing more descriptive and detailed'
    },
    {
      id: 'verb-phrase',
      name: 'Verb Phrase',
      chineseName: '動詞片語',
      definition: 'Main verb with auxiliary verbs',
      structure: 'Auxiliary verb + Main verb',
      examples: [
        'has been studying',
        'will have finished',
        'should have been completed'
      ],
      functions: ['Express tense', 'Show aspect', 'Indicate mood'],
      hkdseNote: 'Essential for complex tense formations'
    },
    {
      id: 'participial-phrase',
      name: 'Participial Phrase',
      chineseName: '分詞片語',
      definition: 'Begins with present or past participle',
      types: [
        {
          type: 'present',
          name: 'Present Participial Phrase',
          examples: [
            'Walking to school, I met my friend.',
            'The student sitting in the front row is smart.',
            'Being tired, she went to bed early.'
          ]
        },
        {
          type: 'past',
          name: 'Past Participial Phrase',
          examples: [
            'Written by Shakespeare, the play is famous.',
            'Exhausted from work, he fell asleep.',
            'The letter sent yesterday arrived today.'
          ]
        }
      ],
      functions: ['Modify nouns', 'Show time/cause/condition'],
      hkdseNote: 'Advanced structure for sophisticated writing'
    },
    {
      id: 'gerund-phrase',
      name: 'Gerund Phrase',
      chineseName: '動名詞片語',
      definition: 'Begins with gerund (verb + ing used as noun)',
      structure: 'Gerund + Object/Complement',
      examples: [
        'Swimming in the ocean is fun.',
        'Reading books improves vocabulary.',
        'His hobby is collecting stamps.'
      ],
      functions: ['Subject', 'Object', 'Complement'],
      hkdseNote: 'Useful for expressing activities and interests'
    },
    {
      id: 'infinitive-phrase',
      name: 'Infinitive Phrase',
      chineseName: '不定詞片語',
      definition: 'Begins with infinitive (to + base verb)',
      structure: 'to + Verb + Object/Complement',
      examples: [
        'To succeed in life requires hard work.',
        'I want to learn English well.',
        'Her goal is to become a doctor.'
      ],
      functions: ['Subject', 'Object', 'Complement', 'Adverbial'],
      hkdseNote: 'Expresses purpose, intention, or future plans'
    }
  ]
};

// Common Errors Data
export const commonErrorsData = {
  subjectVerbAgreement: {
    id: 'subject-verb-agreement',
    title: '主謂一致 (Subject-Verb Agreement)',
    description: 'Subject and verb must agree in number',
    rules: [
      {
        rule: 'Singular subject + singular verb',
        correct: 'The student studies hard.',
        incorrect: 'The student study hard.',
        explanation: 'Student is singular, so use "studies"'
      },
      {
        rule: 'Plural subject + plural verb',
        correct: 'The students study hard.',
        incorrect: 'The students studies hard.',
        explanation: 'Students is plural, so use "study"'
      },
      {
        rule: 'Collective nouns can be singular or plural',
        correct: ['The team is winning.', 'The team are arguing among themselves.'],
        incorrect: 'The team are winning the game.',
        explanation: 'Depends on whether acting as unit or individuals'
      }
    ],
    trickySubjects: [
      {
        type: 'Indefinite pronouns',
        examples: ['everyone', 'someone', 'nobody', 'each', 'either', 'neither'],
        rule: 'Usually singular',
        correct: 'Everyone is here.',
        incorrect: 'Everyone are here.'
      },
      {
        type: 'Subjects with "of"',
        examples: ['one of the students', 'some of the books', 'all of the water'],
        rule: 'Verb agrees with noun after "of"',
        correct: 'One of the students is absent.',
        incorrect: 'One of the students are absent.'
      }
    ],
    hkdseNote: 'Very common in Paper 1 error correction'
  },
  pronounErrors: {
    id: 'pronoun-errors',
    title: '代詞錯誤 (Pronoun Errors)',
    description: 'Incorrect pronoun usage and reference',
    commonMistakes: [
      {
        type: 'Subject vs Object pronouns',
        correct: 'She and I went to the movies.',
        incorrect: 'Her and me went to the movies.',
        rule: 'Use subject pronouns before verbs'
      },
      {
        type: 'Possessive pronouns',
        correct: 'The book is mine.',
        incorrect: 'The book is mines.',
        rule: 'No -s on possessive pronouns'
      },
      {
        type: 'Reflexive pronouns',
        correct: 'I did it myself.',
        incorrect: 'I did it by myself.',
        rule: 'Use reflexive when subject and object are same'
      }
    ],
    hkdseNote: 'Common in cloze passages and error correction'
  },
  tenseConsistency: {
    id: 'tense-consistency',
    title: '時態一致性 (Tense Consistency)',
    description: 'Maintaining consistent tense throughout text',
    rules: [
      {
        rule: 'Keep same tense for same time period',
        correct: 'Yesterday I went to school and met my friend.',
        incorrect: 'Yesterday I went to school and meet my friend.',
        explanation: 'Both actions happened yesterday - use past tense'
      },
      {
        rule: 'Change tense when time changes',
        correct: 'I studied hard last year, and now I am successful.',
        incorrect: 'I studied hard last year, and now I was successful.',
        explanation: 'Past action, present result'
      }
    ],
    hkdseNote: 'Important for narrative writing in Paper 2'
  },
  punctuationErrors: {
    id: 'punctuation-errors',
    title: '標點符號 (Punctuation)',
    description: 'Correct punctuation usage',
    rules: [
      {
        mark: 'Comma (,)',
        uses: [
          'Separate items in a list',
          'Before coordinating conjunctions',
          'After introductory elements',
          'Around non-essential information'
        ],
        examples: [
          'I bought apples, oranges, and bananas.',
          'I studied hard, but the exam was difficult.',
          'After the movie, we went home.',
          'My brother, who lives in Canada, is visiting.'
        ]
      },
      {
        mark: 'Apostrophe (\')',
        uses: [
          'Show possession',
          'Contractions'
        ],
        examples: [
          'The student\'s book (one student)',
          'The students\' books (multiple students)',
          'It\'s raining (it is)',
          'They\'re coming (they are)'
        ],
        commonErrors: ['its vs it\'s', 'whose vs who\'s']
      }
    ],
    hkdseNote: 'Essential for Paper 2 writing assessment'
  }
};

// Practice Exercises Data
export const practiceExercisesData = {
  partsOfSpeechExercises: [
    {
      id: 'pos-identification-1',
      type: 'identification',
      title: 'Identify Parts of Speech',
      instruction: 'Identify the part of speech for each underlined word.',
      questions: [
        {
          sentence: 'The *intelligent* student *quickly* *solved* the *difficult* problem.',
          answers: [
            { word: 'intelligent', pos: 'adjective', explanation: 'Describes the student' },
            { word: 'quickly', pos: 'adverb', explanation: 'Describes how the student solved' },
            { word: 'solved', pos: 'verb', explanation: 'Action word' },
            { word: 'difficult', pos: 'adjective', explanation: 'Describes the problem' }
          ]
        }
      ],
      hkdseRelevance: 'Paper 1 - Grammar section'
    }
  ],
  tenseExercises: [
    {
      id: 'tense-completion-1',
      type: 'completion',
      title: 'Complete with Correct Tense',
      instruction: 'Fill in the blanks with the correct tense of the verb in brackets.',
      questions: [
        {
          sentence: 'I _____ (study) English for five years.',
          correctAnswer: 'have been studying',
          explanation: 'Present perfect continuous - action started in past and continues',
          options: ['study', 'studied', 'have studied', 'have been studying']
        },
        {
          sentence: 'When I _____ (arrive) at the station, the train _____ (already/leave).',
          correctAnswer: 'arrived, had already left',
          explanation: 'Past perfect for action completed before another past action',
          options: ['arrive, leaves', 'arrived, had already left', 'arrive, had left', 'arrived, already left']
        }
      ],
      hkdseRelevance: 'Paper 1 - Cloze passage'
    }
  ],
  errorCorrectionExercises: [
    {
      id: 'error-correction-1',
      type: 'correction',
      title: 'Correct the Errors',
      instruction: 'Find and correct the grammatical errors in each sentence.',
      questions: [
        {
          sentence: 'Each of the students have their own book.',
          error: 'have',
          correction: 'has',
          explanation: '"Each" is singular, so use "has"',
          errorType: 'Subject-verb agreement'
        },
        {
          sentence: 'Me and my friend went to the movies.',
          error: 'Me and my friend',
          correction: 'My friend and I',
          explanation: 'Use subject pronouns before verbs',
          errorType: 'Pronoun error'
        }
      ],
      hkdseRelevance: 'Paper 1 - Error correction section'
    }
  ]
};

// HKDSE Exam Tips
export const hkdseExamTips = {
  paper1: {
    title: 'Paper 1: Reading',
    tips: [
      'Read questions before reading passage',
      'Look for grammar patterns in cloze exercises',
      'Check subject-verb agreement carefully',
      'Pay attention to tense consistency',
      'Use process of elimination for multiple choice'
    ],
    grammarFocus: [
      'Verb tenses and aspects',
      'Subject-verb agreement',
      'Pronoun reference',
      'Prepositions and conjunctions',
      'Relative clauses'
    ]
  },
  paper2: {
    title: 'Paper 2: Writing',
    tips: [
      'Use variety of sentence structures',
      'Check grammar and punctuation',
      'Use appropriate tenses consistently',
      'Avoid repetitive vocabulary',
      'Plan your writing structure'
    ],
    grammarFocus: [
      'Complex sentence structures',
      'Appropriate tense usage',
      'Correct punctuation',
      'Varied sentence beginnings',
      'Proper paragraph structure'
    ]
  },
  paper4: {
    title: 'Paper 4: Speaking',
    tips: [
      'Use correct grammar in responses',
      'Practice common question patterns',
      'Use appropriate tenses for different topics',
      'Avoid basic grammar mistakes',
      'Use connecting words effectively'
    ],
    grammarFocus: [
      'Question formation',
      'Appropriate tense usage',
      'Conditional sentences',
      'Reported speech',
      'Linking words and phrases'
    ]
  },
  sba: {
    title: 'SBA: School-based Assessment',
    tips: [
      'Proofread for grammar errors',
      'Use sophisticated sentence structures',
      'Maintain tense consistency',
      'Check punctuation carefully',
      'Use appropriate register'
    ],
    grammarFocus: [
      'Advanced sentence structures',
      'Correct punctuation',
      'Appropriate vocabulary',
      'Consistent style',
      'Error-free writing'
    ]
  }
};

// Visual Learning Features
export const visualFeatures = {
  colorCoding: {
    nouns: '#3B82F6',      // Blue
    verbs: '#10B981',      // Green  
    adjectives: '#F59E0B', // Orange
    adverbs: '#8B5CF6',    // Purple
    pronouns: '#EF4444',   // Red
    prepositions: '#6B7280', // Gray
    conjunctions: '#84CC16', // Lime
    interjections: '#EC4899' // Pink
  },
  icons: {
    grammar: '📝',
    tenses: '⏰',
    structure: '🏗️',
    errors: '⚠️',
    practice: '🎯',
    tips: '💡'
  }
};

export default {
  grammarCategories,
  partsOfSpeechData,
  verbTensesData,
  sentenceStructureData,
  clausesPhrasesData,
  commonErrorsData,
  practiceExercisesData,
  hkdseExamTips,
  visualFeatures
}; 